import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@i18n';
import { Rect, Ellipse, Line, Text, Image, PropertyEvent, Polygon } from 'leafer-ui';
import { Arrow } from '@leafer-in/arrow';
import debounce from 'lodash/debounce';
import { numSvg } from '@utils/utils';
import Magnifier from '@utils/shape/Magnifier';

// 注册Text组件以确保它在UICreator中可用
try {
    // 确保Text组件被正确初始化和注册
    if (!Text._register) {
        Text.register('Text');
        Text._register = true;
    }
} catch (e) {
    // 使用 i18n 实例获取翻译
    console.warn(i18n.t('editor:error.registerTextComponent'), e);
}

export default ({ parent, type, id, width, height, x, y, fill, strokeWidth, zIndex, points, editable, text, snap, fontSize, fontFamily, fontStyle, textDecoration, backgroundColor, rotation, src }) => {
    const { t } = useTranslation('editor');
    const shape = useMemo(() => {
        const defaultOption = { id, x, y, zIndex }
        if (type === 'SquareFill') {
            return new Rect({
                cornerRadius: 8,
                width,
                height,
                fill,
                ...defaultOption
            });
        }
        if (type === 'Circle') {
            return new Ellipse({
                stroke: fill,
                strokeWidth,
                width,
                height,
                ...defaultOption
            });
        }
        if (type === 'Sparkles') {
            // 五角星图形
            return new Polygon({
                stroke: fill,
                strokeWidth,
                sides: 5,
                sideIndent: 0.4, // 星形缩进比例
                width,
                height,
                ...defaultOption
            });
        }
        if (type === 'Sunset') {
            // 椭圆图形
            return new Ellipse({
                fill,
                width,
                height: height * 0.6, // 使其呈现为椭圆形
                ...defaultOption
            });
        }
        if (type === 'Maximize') {
            // 多边形图形 (正六边形)
            return new Polygon({
                stroke: fill,
                strokeWidth,
                sides: 6,
                width,
                height,
                ...defaultOption
            });
        }
        if (type === 'Magnifier') {
            return new Magnifier({
                stroke: '#ffffff90',
                strokeWidth,
                strokeAlign: 'outside',
                width,
                height,
                shadow: {
                    x: 4,
                    y: 4,
                    blur: 6,
                    color: '#00000010',
                    box: true
                },
                ...defaultOption
            });
        }
        if (type === 'Slash') {
            return new Line({
                id,
                points,
                zIndex,
                stroke: fill,
                strokeWidth
            });
        }
        if (type === 'MoveDownLeft') {
            return new Arrow({
                id,
                points,
                zIndex,
                strokeCap: 'round',
                strokeJoin: 'round',
                stroke: fill,
                strokeWidth
            });
        }
        if (type === 'Edit3') {
            // 虚线
            return new Line({
                id,
                points,
                zIndex,
                stroke: fill,
                strokeWidth,
                dash: [10, 5], // 虚线样式
                strokeCap: 'round', // 线段端点为圆形
                strokeJoin: 'round' // 线段连接处为圆形
            });
        }
        if (type === 'Crop') {
            // 折线
            return new Line({
                id,
                points,
                zIndex,
                stroke: fill,
                strokeWidth,
                curve: false,
                closed: false
            });
        }
        if (type === 'ArrowDownRight') {
            // 弧线 - 直接使用曲线配置
            return new Line({
                id,
                points,
                zIndex,
                stroke: fill,
                strokeWidth,
                curve: true, // 启用曲线
                // 使用贝塞尔曲线来创建明显的弧线效果
                curveType: 'quadratic', // 二次贝塞尔曲线更适合弧线
                // 曲线控制参数会在View.jsx中的DRAG事件中动态设置
                strokeCap: 'round',
                strokeJoin: 'round'
            });
        }
        if (type === 'Pencil') {
            return new Line({
                id,
                points,
                zIndex,
                curve: true,
                stroke: fill,
                strokeWidth
            });
        }
        if (type === 'Step') {
            return new Ellipse({
                ...defaultOption,
                width: 32,
                height: 32,
                stroke: '#ffffff90',
                strokeWidth,
                strokeAlign: 'outside',
                lockRatio: true,
                shadow: {
                    x: 1,
                    y: 1,
                    blur: 2,
                    color: '#00000045',
                    box: true
                },
                fill: [
                    {
                        type: 'solid',
                        color: fill
                    },
                    {
                        type: 'image',
                        url: numSvg(text),
                        format: 'svg',
                        align: 'center'
                    }
                ]
            })
        }
        if (type === 'emoji') {
            return new Text({
                id,
                zIndex,
                text,
                resizeFontSize: true,
                fontSize: 48
            });
        }
        if (type === 'sticker') {
            return new Text({
                ...defaultOption,
                text: text || '😊',
                fontSize: parseInt(fontSize) || 48,
                editable: true,
                resizable: true,
                movable: true,
                rotateable: true,
                rotation: rotation || 0,
                resizeFontSize: true,
                lockRatio: true,
                autoSize: true,
                padding: [4, 4]
            });
        }
        if (type === 'image') {
            return new Image({
                ...defaultOption,
                url: src,
                width: width || 80,
                height: height || 80,
                editable: true,
                resizable: true,
                movable: true,
                rotateable: true,
                rotation: rotation || 0,
                lockRatio: true,
                shadow: {
                    x: 2,
                    y: 2,
                    blur: 5,
                    color: '#00000015',
                    box: true
                }
            });
        }
        if (type === 'Text') {
            return new Text({
                ...defaultOption,
                text: text || t('textTool.defaultText'),
                fontSize: parseInt(fontSize) || 24,
                fontFamily: fontFamily || 'sans-serif',
                fontStyle: fontStyle || 'normal',
                textDecoration: textDecoration || 'none',
                fill: fill || '#000000',
                backgroundColor: backgroundColor === 'transparent' ? undefined : backgroundColor,
                textAlign: 'left',
                rotation: rotation || 0,
                editable: true,
                resizable: true,
                movable: true,
                rotateable: true,
                resizeFontSize: true,
                lockRatio: false,
                autoSize: false,
                padding: [4, 8],
                minWidth: 20,
                minHeight: 20
            });
        }
        return new Rect({
            cornerRadius: 8,
            stroke: fill,
            strokeWidth,
            width,
            height,
            ...defaultOption
        });
    }, [parent]);

    useEffect(() => {
        if (['Slash', 'MoveDownLeft', 'Pencil', 'Crop'].includes(type)) {
            shape.points = points;
        } else if (type === 'Step') {
            //
        } else if (type === 'Text') {
            shape.x = x;
            shape.y = y;
            shape.text = text || t('textTool.defaultText');
            shape.fontSize = parseInt(fontSize) || 24;
            shape.fontFamily = fontFamily || 'sans-serif';
            shape.fontStyle = fontStyle || 'normal';
            shape.textDecoration = textDecoration || 'none';
            shape.fill = fill || '#000000';
            shape.backgroundColor = backgroundColor === 'transparent' ? undefined : backgroundColor;
            shape.rotation = rotation || 0;
            shape.resizable = true;
            shape.movable = true;
            shape.rotateable = true;
            shape.resizeFontSize = true;
            shape.lockRatio = false;
            shape.autoSize = false;
            shape.padding = [4, 8];
            shape.minWidth = 20;
            shape.minHeight = 20;
        } else if (type === 'sticker') {
            shape.x = x;
            shape.y = y;
            shape.text = text || '😊';
            shape.fontSize = parseInt(fontSize) || 48;
            shape.rotation = rotation || 0;
            shape.resizable = true;
            shape.movable = true;
            shape.rotateable = true;
            shape.resizeFontSize = true;
            shape.lockRatio = true;
            shape.autoSize = true;
            shape.padding = [4, 4];
        } else if (type === 'image') {
            shape.x = x;
            shape.y = y;
            shape.url = src;
            shape.width = width || 80;
            shape.height = height || 80;
            shape.rotation = rotation || 0;
            shape.resizable = true;
            shape.movable = true;
            shape.rotateable = true;
            shape.lockRatio = true;
        } else {
            shape.x = x;
            shape.y = y;
            shape.width = width;
            shape.height = height;
        }
    }, [x, y, width, height, text, fontSize, fontFamily, fontStyle, textDecoration, backgroundColor, rotation, src]);

    useEffect(() => {
        if (type === 'SquareFill') shape.fill = fill;
        if (['Circle', 'Slash', 'MoveDownLeft', 'Pencil', 'Square', 'Sparkles', 'Maximize', 'Crop'].includes(type)) shape.stroke = fill;
        if (type === 'Step') {
            const oldFill = [].concat(shape.fill);
            oldFill[0].color = fill;
            shape.fill = oldFill;
        }
        if (type === 'Text') {
            shape.fill = fill || '#000000';
        }
        if (type === 'Sunset') {
            shape.fill = fill;
        }
    }, [fill]);

    useEffect(() => {
        if (['Circle', 'Magnifier', 'Slash', 'MoveDownLeft', 'Pencil', 'Step', 'Square', 'Sparkles', 'Sunset', 'Maximize', 'Crop'].includes(type)) shape.strokeWidth = strokeWidth;
    }, [strokeWidth]);

    useEffect(() => {
        shape.editable = !!editable;
    }, [editable]);

    useEffect(() => {
        if (type === 'Magnifier' && shape.fill && snap) {
            const oldFill = [].concat(shape.fill);
            oldFill[1] = Object.assign({}, oldFill[1], { url: snap.data, size: { width: snap.width, height: snap.height } });
            shape.fill = oldFill;
        }
        const offset = {x:0,y:0};
        const fillBg = debounce(() => {
            const x = -shape.x * 2 - shape.width / 2;
            const y = -shape.y * 2 - shape.height / 2;
            if (offset.x === x && offset.y === y) return;
            offset.x = x;
            offset.y = y;
            shape.fill = [
                { type: 'solid', color: '#ffffff' },
                {
                    type: 'image',
                    url: snap.data,
                    mode: 'clip',
                    size: {
                        width: snap.width,
                        height: snap.height
                    },
                    offset
                },
                {
                    type: 'linear',
                    from: 'top',
                    to: 'bottom',
                    stops: [
                        { offset: 0, color: '#ffffffaa' },
                        { offset: 0.48, color: '#ffffff00'}
                    ]
                }
            ];
        }, 5);
        shape.on(PropertyEvent.CHANGE, (arg) => {
            if (!snap?.data) return;
            if (!['x', 'y', 'width', 'height'].includes(arg.attrName)) return;
            fillBg();
        });
        return (() => {
            shape.off(PropertyEvent.CHANGE);
        })
    }, [snap]);

    useEffect(() => {
        parent.add(shape);
        return (() => {
            shape.remove();
        })
    }, [parent]);
    return null;
}